import type {SbRichTextDoc as ISbRichtext} from "@storyblok/js";

// Comes from nuxt-jsonld
import type {Thing, WithContext, Graph} from "schema-dts";
import type {NuxtError as NativeNuxtError} from "nuxt/app";

import type {
	SimpleProductDataFragment,
	ViewProductDetailsQuery,
} from "../graphql";
import type {Icon} from "./icons";
import type {PayFrameInitFlags, PayFrameInstance} from "./avarda";

export {type Thing} from "schema-dts";

export {type Icon} from "./icons";

export * from "./ga";

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Avarda's SDK ships no types
		avardaCheckoutInit?: any;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Avarda's SDK ships no types
		avardaPaymentInit?: any;

		Auth0Lock?: Auth0LockStatic;
		avardaPayFrameInit?: ((initFlags: PayFrameInitFlags) => void) & {
			q?: [PayFrameInitFlags][];
		};
		avardaPayFrameInstance?: PayFrameInstance;
	}
}

export type NuxtError = NativeNuxtError;

export type JsonLD = WithContext<Thing> | WithContext<Thing>[] | Graph | null;

export type Coordinates = {
	latitude: number;
	longitude: number;
};

export interface State {
	isLoading: boolean;
	articles: string[];
	loadedArticles: number;
}

declare module "csstype" {
	interface Properties {
		[variableName: `--${string}`]: string | number | undefined;
	}
}

export type ProductKind<T> =
	| Extract<T, {__typename: "SimpleProduct"}>
	| Extract<T, {__typename: "ConfigurableProduct"}>;

export type ToastTheme = "info" | "success" | "warning" | "danger";

export interface Toast {
	id: number;
	theme: ToastTheme;
	text: string;
}

export interface DropdownOption<
	T extends string | number = string | number,
	M = unknown,
> {
	title: string;
	value: T;
	disabled?: boolean;
	meta?: M;
}

export type ImageType = "model" | "cutout";

export interface ProductCard {
	id: number;
	image: string;
	name: string;
	description: string;
	pricing: Price;
	salePrice: number;
	shipping?: string;
	brand?: string;
	tags?: string[];
	campaign?: string;
	saleText?: boolean;
	themeColor?: string;
	listingImage?: ProductListingImage;
}

export interface ProductListingImage {
	__typename: string;
	id: string;
	url: string;
}

export interface Price {
	price: ProductPrice;
}

export interface ProductPrice {
	__typename: string;
	amount: string;
	currencyCode: string;
}

export interface OrderProduct {
	__typename: string;
	id: string;
	listingImage: ProductListingImage;
	name: string;
	sku: string;
}

export interface OrderProducts {
	id: string;
	price: ProductPrice;
	quantity: number;
	taxRate: number;
	product: OrderProduct;
}

export interface Address {
	id: string;
	firstName: string;
	lastName: string;
	address: string;
	postalCode: string;
	city: string;
	country: string;
	phone: string;
	countryCode: string;
}

interface OrderPrice {
	__typename?: string;
	amount: string;
}

export interface CustomerOrder {
	__typename?: string;
	node: CustomerOrderNode;
}

export interface CustomerOrderNode {
	createdAt: string;
	id: string;
	number: number;
	state: string;
	totalPrice: OrderPrice;
}

export interface CustomerCompany {
	__typename?: string;
	id: string;
	name: string;
}

export interface Customer {
	id: string;
	number: number;
	firstName: string;
	lastName: string;
	__typename?: string;
	company?: CustomerCompany;
	businessId?: string;
	customerNumber?: number;
	addresses: Address[];
	email: string;
	phone: string;
	updatedAt: string;
	orders?: CustomerOrder[];
	role?: string;
}

export interface Auth0Profile {
	id: string;
	email: number;
	name: string;
	picture: string;
}

export interface ProductIdProduct {
	__typename: string;
	id: string;
	name: string;
}

export interface ProductIdAttribute {
	key: string;
	value: ProductIdProduct[];
}

export interface ProductId {
	group: string;
	attribute: ProductIdAttribute;
}

export interface Delivery {
	address: string;
	postalCode: string;
	city: string;
	fee: number;
	type: string;
}

export interface Payment {
	method: string;
	fee: number;
}

export interface OrderData {
	status: string;
	date: Date;
	amount: string;
	orderNo: number;
	processed: Date;
	customer: Customer;
	delivery: Delivery;
	payment: Payment;
	products: OrderProduct[];
}

export interface OrderCard {
	products: OrderProduct[];
	status: string;
	date: Date;
	amount: string;
	orderNo: number;
}
export interface DisclosureOption {
	attr: string;
	title: string;
	value: string;
	sizes: string[];
}

export interface RadioGroupItemAddress {
	id: number;
	firstName: string;
	lastName: string;
	role?: string;
	company?: string;
	businessId?: string;
	customerNumber?: number;
	street: string;
	postalCode: string;
	city: string;
	providence?: string;
	countryCode?: string;
	email: string;
	phone: string;
	isBilling?: string | boolean;
	isCompany?: string | boolean;
	default?: string | boolean;
}

export interface BankOption {
	logo?: string;
	title?: string;
}

export interface CardOption {
	logo?: string;
	title?: string;
}

export interface RadioGroupPayment {
	value?: string;
	title?: string;
	hint?: string;
	details?: string[];
	showInput?: boolean;
	prompt?: string;
	ingress?: string;
	description?: string;
	bankOptions?: BankOption[];
	cardOptions?: CardOption[];
	logo?: Icon;
	address?: RadioGroupItemAddress;
}

export interface RadioGroupMethod {
	name: string;
	title: string;
	value: string;
	price: number;
	prompt: string;
	logo?: Icon;
}

export interface RadioGroupItem {
	id?: string;
	name?: string;
	title?: string;
	value?: string | number;
	hex?: string;
	text?: string;
	ingress?: string;
	details?: string[];
	showInput?: boolean;
	description?: string;
	logo?: Icon | string;
	price?: number;
	prompt?: string;
	hint?: string;
	products: string[];
}

export interface Parcels {
	itemId?: string;
	parcelNumber?: string;
	height?: string;
	length?: string;
	width?: string;
	volume?: string;
	weight?: string;
	quantity?: string;
	itemQuantity?: string;
}

type ProductDetailsProduct = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;

export type ProductDetailsPromotionStyle =
	ProductDetailsProduct["promotions"][number]["style"];

export interface PromotionInfo {
	activeTo?: Date;
	hidePriceHistory: boolean;
	showEndingTimer: boolean;
	showCountdownTextContent: boolean;
	promotionTimerContent: string;
	deliveryPricingInfoOverride: string;
	omnibusContent: string;
	isTimedVisibility: boolean;
	isCampaignModalEnabled: boolean;
	campaignModalContent: string;
	style?: ProductDetailsPromotionStyle;
}

// !!!
/* eslint-disable @typescript-eslint/naming-convention */
// !!!

export interface OpenJobData {
	jobs: OpenJob[];
	generated_at: string;
}

export interface OpenJob {
	id: string;
	name: string;
	link: string;
	duedate: string;
}

export type ButtonThemes =
	| "black"
	| "gray"
	| "danger"
	| "white"
	| "checkout"
	| "red"
	| "green"
	| "orange"
	// Sotka-only for now: only apps/sotka's button.vue defines a `blue` variant.
	| "blue";

export interface ProductRecommendationsContent {
	_uid: string;
	component: string;
	recommendation_id: string;
	article_categories: string[];
}

interface Discount {
	type: string;
	amount: number;
}

interface Colors {
	color: string;
	customColor: string | null;
}

interface Font {
	family: string;
	size: string;
	customSize: number | null;
}

interface Theme {
	background?: Colors;
	text?: Colors;
	font?: Font;
}

interface Style {
	type:
		| "TEXT"
		| "PERCENT"
		| "PERCENT_WITH_AMOUNT"
		| "PERCENT_WITH_TEXT"
		| "CLUB_PERCENT_WITH_TEXT"
		| "OMNIBUS";
	content: string | null;
	mirrored: boolean;
	altContent: string | null;
	mainTheme?: Theme;
	altTheme?: Theme;
}

export interface PromotionData {
	discount: Discount;
	style: Style;
}

export interface ConsentCookie {
	categories: string[];
	level: string[];
	revision: number;
	data: unknown;
	consent_date: string;
	consent_uuid: string;
	last_consent_update: string;
}

export interface PopupCookie {
	isPopupClosed: boolean;
	expires: Date;
}

export interface ElevateProductBadge {
	id: string;
	title: string;
	listingTitle: string;
	description: string;
	backgroundColor: string;
	textColor: string;
	icon?: string;
	transparentBackground?: boolean;
}

/// /////////////////////////////////////////////////////////////////////////////
// Storyblok related stuff, should move into their own file
/// /////////////////////////////////////////////////////////////////////////////

// Not provided by the SDK for some reason
export interface SbDatasourceEntry {
	id: number;
	name: string;
	value: string;
	dimension_value: string;
}

// Storyblok story object
export interface Story {
	alternates: string[];
	content: BlokComponent;
	created_at: string;
	default_full_slug: string;
	first_published_at: string;
	full_slug: string;
	group_id: string;
	id: number;
	is_startpage: boolean;
	lang: string;
	meta_data: string | null;
	name: string;
	parent_id: number;
	path: string;
	position: number;
	published_at: string;
	release_id: string | null;
	slug: string;
	sort_by_date: string | null;
	tag_list: string[];
	translated_slugs: null;
	uuid: string;
}

// Storyblok colorpicker
export interface BlokColorpicker {
	_uid: string;
	color: string;
	plugin: string;
}

export interface BlokSliderPlugin {
	_uid: string;
	value: number;
	plugin: string;
}

// Storyblok image
export interface BlokAsset {
	_uid: string;
	alt: string;
	copyright: string;
	fieldtype: string;
	filename?: string;
	focus: string | null;
	id: string;
	name: string;
	title: string;
	source: string;
}

// Storyblok link
export interface BlokLink {
	cached_url: string;
	anchor?: string;
	fieldtype: string;
	id: string;
	linktype: string;
	url?: string;
	Links: [];
	target?: string;
	story?: {
		full_slug: string;
		url: string;
	};
}

// Stroyblok richtext component content (paragrahs etc)
export interface BlokRichText {
	type: string;
	content: string[];
}
// Storyblok richtext component
export interface RichText {
	_editable: string;
	_uid: string;
	body: string[];
	content: string[];
	name: string;
	component: string;
	type: string;
	marks?: string[];
	attrs?: string[];
	text?: string;
}

export interface SbLink {
	_editable: string;
	_uid: string;
	component: string;
	icon: Icon;
	url: string;
	link: BlokLink;
}

export interface IHoursTableBody {
	value: string;
	_uid: string;
	component: string;
}

export interface ITableElements {
	body: IHoursTableBody[];
}

export interface IHoursTable {
	fieldtype: string;
	thead: ITableElements;
	tbody: ITableElements[];
}

export interface BrandBlokContent {
	_editable: string;
	_uid: string;
	component: string;
	image: BlokAsset;
	title: string;
	name: string;
	content: BrandCardBlokComponent;
}

export interface BlokComponentBody {
	_uid: string;
	brands?: [];
	brand?: string[];
	article?: string[];
	articles?: string[];
	component: string;
	title?: string;
}

export interface BaseBlokComponent {
	_editable: string;
	_uid: string;
	created_at: string;
	body: BlokComponentBody[];
	name?: string;
	component?: string;
	title?: string;
	content?: ArticleBlokContent;
	content_color?: BlokColorpicker;
	image: BlokAsset;
	link: BlokLink;
	text_color?: BlokColorpicker;
	background_color?: BlokColorpicker;
	articles?: string[];
	tags: string;
	ingress: string;
	nosto_id: string;
	background_styles: string[];
	content_alignment: string;
	button_link: BlokLink;
	button_text: string;
	meta?: MetaFieldsPlugin;
	seo_image?: BlokAsset;
}

export interface ArticlesBlokComponent {
	component: "sb-page-articles";
	articles?: BlokComponentBody[];
}

export interface OpenJobsBlokcomponent {
	component: "sb-open-jobs";
	jobs_uri: "string";
	_uid: string;
}

export interface LanguageBlokComponent {
	component: "sb-languages";
	_uid: string;
	name: string;
	icon: Icon;
	ingress: string;
}

export interface ArticleBlokComponent {
	component: "sb-article";
	_uid?: string;
	name: string;
	created: string;
	image: BlokAsset;
	tags: string;
	title: string;
}

export interface ArticleUuids {
	id: string;
}

export interface ArticlesFeedBlokComponent {
	component: "sb-article-feed-side";
	article: ArticleUuids[];
	articles: ArticleUuids[];
}

export interface ArticleLinks {
	component: "sb-article";
	name: string;
	created: string;
	image: BlokAsset;
	tags: string;
	title: string;
}

export interface ArticleLink {
	id: number;
	is_folder: boolean;
	is_startpage: boolean;
	name: string;
	parent_id: number;
	path: string | null;
	position: number;
	published: boolean;
	real_path: string;
	slug: string;
	uuid: string;
}

export interface CtaBlokComponent {
	component:
		| "sb-cta"
		| "sb-cta-narrow"
		| "sb-cta-big"
		| "sb-cta-button"
		| "sb-cta-content"
		| "sb-cta-image"
		| "sb-cta-countdown";
	_uid: string;
	name: string;
	title: string;
	subtitle: string;
	button_text: string;
	link?: BlokLink;
	link_text?: string;
	button_color?: BlokColorpicker;
	cta_link: BlokLink;
	button: BlokLink;
	content_alignment: string;
	image: BlokAsset;
	image_mobile?: BlokAsset;
	bg_image: BlokAsset;
	content: ISbRichtext;
	step_number: number;
	text_alignment?: string;
	background_color?: BlokColorpicker;
	nosto_id?: string;
	background_position?: string;
	button_text_color: BlokColorpicker;
	button_background_color: BlokColorpicker;
	whole_element_cta_link?: BlokLink;
	only_button_as_link?: boolean;
	index?: number;
	counter?: PromotionCountdownComponent[];
	header_color?: BlokColorpicker;
	cta_background_color?: BlokColorpicker;
	cta_text_color?: BlokColorpicker;
	text_color?: BlokColorpicker | string;
	use_link_as_login: boolean;
	enable_background_animation?: boolean;
}

export interface CtaSimpleWithDiscountBlokComponent {
	component: "sb-cta-simple-with-discount";
	_uid: string;
	title: string;
	subtitle: string;
	background_image: BlokAsset;
	content: ISbRichtext;
	title_font_size: number;
	title_font_color: BlokColorpicker;
	sub_title: string;
	sub_title_font_size: number;
	sub_title_font_color: BlokColorpicker;
	button_link: BlokLink;
	button_text: string;
	button_font_size: number;
	button_font_color: BlokColorpicker;
	button_background_color: BlokColorpicker;
	discount: string;
	discount_amount: ISbRichtext;
	discount_font_size: number;
	discount_font_color: BlokColorpicker;
	discount_background_color: BlokColorpicker;
	disclaimer: string;
	disclaimer_font_size: number;
	disclaimer_font_color: BlokColorpicker;
	enable_shadow: boolean;
	enable_background_darkening: boolean;
}

export interface CtaCampaignHeroBox {
	component: "sb-cta-campaign-hero-box";
	title: string;
	title_font_color: BlokColorpicker;
	ingress: ISbRichtext;
	ingress_font_color: BlokColorpicker;
	background_image: BlokAsset;
	background_image_mobile: BlokAsset;
	background_color: BlokColorpicker;
	link: BlokLink;
	link_color: BlokColorpicker;
	link_text: string;
	link_text_color: BlokColorpicker;
	enable_background_darkening: boolean;
	enable_text_shadow: boolean;
	content_align: string;
	enable_gradient: boolean;
	gradient_from: BlokColorpicker;
	gradient_to: BlokColorpicker;
	show_logo: boolean;
	percentage: string;
	percent_color: BlokColorpicker;
	percent_size: string;
	content_alignment: string;
}

export interface CtaCampaignHero {
	component: "sb-cta-campaign-hero";
	title: string;
	title_font_color: BlokColorpicker;
	ingress: ISbRichtext;
	ingress_font_color: BlokColorpicker;
	background_image: BlokAsset;
	background_image_mobile: BlokAsset;
	background_color: BlokColorpicker;
	discount: BlokComponentBody[];
	hero_boxes: CtaCampaignHeroBox[];
	button: BlokLink;
	button_text: string;
	button_theme: "black" | "red" | "green" | "orange";
	enable_background_darkening: boolean;
	enable_text_shadow: boolean;
	content_align: string;
	percentage: string;
	percent_color: BlokColorpicker;
	percent_size: string;
	enable_gradient: boolean;
	gradient_from: BlokColorpicker;
	gradient_to: BlokColorpicker;
	show_logo: boolean;
	background_video: VimeoPlugin;
	video_fallback: BlokAsset;
}

export interface CategoryCtaBlokComponent {
	component: "category-cta";
	description: string;
	button_text: string;
	button_color: BlokColorpicker;
	button_link: BlokLink;
	link_text: string;
	text_color: BlokColorpicker;
	background_color: BlokColorpicker;
	enabled: boolean;
	on_pages: string[];
	_uid: string;
}

export interface LiftBlokComponent {
	component: "sb-lift-big" | "sb-lift-normal";
	_uid: string;
	title: string;
	image?: BlokAsset;
	background_color?: BlokColorpicker;
	text_color?: BlokColorpicker;
	link?: BlokLink;
	body?: BlokComponentBody[];
}

export interface BrandBlokComponent {
	component: "sb-brand";
	_uid?: string;
	title: string;
	link?: BlokLink;
	image?: BlokAsset;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	name: string;
	introduction: string;
}

export interface BrandCardBlokComponent {
	component: "sb-brand-card-big" | "sb-brand-card";
	title: string;
	link?: BlokLink;
	image?: BlokAsset;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	name: string;
}

export interface CustomerServiceTitleBlokComponent {
	component: "sb-customer-service-title";
	title?: string;
	icon: Icon;
	_uid: string;
}

export interface CustomerServiceContentBlokComponent {
	component: "sb-customer-service-content";
	title?: string;
	icon: Icon;
	content: ISbRichtext;
	_uid: string;
}

export interface BrandsBlokComponent {
	component: "sb-brands-grid" | "sb-brands-grid-brand";
	_uid: string;
	title: string;
	link?: BlokLink;
	image?: BlokAsset;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	content: ISbRichtext;
	introduction: string;
}

export interface DiscountsBlokComponent {
	component: "sb-discounts-grid" | "sb-discounts-grid-item";
	_uid: string;
	title: string;
	discounts: DiscountBlokContent[];
	link?: BlokLink;
	image?: BlokAsset;
	image_background_color?: BlokColorpicker;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	content: ISbRichtext;
	introduction: string;
	text_content_color: BlokColorpicker;
	text_content_background: BlokColorpicker;
	valid_to: string;
	button_link: BlokLink;
	button_text: string;
}

export interface BrandsListingBlokComponent {
	component: "sb-brands-listing" | "sb-brands-listing-big";
	id: string;
	_uid: string;
	title: string;
	link?: BlokLink;
	image?: BlokAsset;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	content: BrandBlokContent[];
}

export interface NostoBlockComponent {
	component: "sb-nosto";
	_uid: string;
	title?: string;
	ingress?: string;
	nosto_id: string;
	titles_align?: string;
	products?: CmdProductListPlugin;
	button_link?: BlokLink;
	button_text?: string;
}

export interface CmdProductListPlugin {
	_uid: string;
	title: "CMD product list plugin";
	plugin: "cmd-product-list";
	source?: {
		id: string;
		type: string;
	};
	description: string;
}
export interface StoryblokSliderPlugin {
	plugin: string;
	value: number;
}

export interface MetaFieldsPlugin {
	_uid: string;
	title: string;
	plugin: "meta-fields";
	description: string;
}

export interface CtaDiscountBlokComponent {
	component: "sb-cta-discount";
	_uid: string;
	title: string;
	content_alignment: string;
	cta_title: ISbRichtext;
	cta_link?: BlokLink;
	button_type?: string;
	button_text?: string;
	background?: BlokAsset;
	products?: CmdProductListPlugin;
	discount: BlokComponentBody[];
	nosto_id?: string;
	background_color?: BlokColorpicker;
	background_style?: string;
	terms?: string;
	text_color?: BlokColorpicker;
}

export interface SlideBlokComponent {
	component: "sb-campaign-slides" | "sb-campaign-slide";
	title: string;
	image?: BlokAsset;
	ingress: string;
	background_color?: BlokColorpicker;
	text_color?: BlokColorpicker;
	link?: BlokLink;
	body?: BlokComponentBody[];
	slides: BlokComponent[];
	_uid: string;
}

export interface ArticleBlokContent extends BaseBlokComponent {
	name: string;
	created_at: string;
	type: string;
}

export interface LinkBlokContent {
	component: "sb-link";
	_uid: string;
	link: BlokLink;
	name: string;
}

export interface DropdownBlokContent {
	component: "sb-dropdown";
	title: string;
	dropdown_content: BlokComponent[];
	_uid: string;
}

export interface DropdownContentBlokContent {
	component: "sb-dropdown-content";
	title: string;
	description: string;
	image: BlokAsset;
	dropdown_content: ISbRichtext;
	payment_options?: BlokAsset[];
	tag?: string;
	_uid: string;
}

export interface ProductHighlightInfoBlokContent {
	component: "sb-product-highlight-info";
	_uid: string;
	background?: BlokAsset;
	link?: BlokLink;
	title: string;
	content: ISbRichtext;
	background_color?: BlokColorpicker;
	text_color?: string;
}

export interface ProductHighlightsBlokContent {
	component: "sb-product-highlights";
	_uid: string;
	content: ProductHighlightBlokContent[];
}

export interface ProductHighlightBlokContent {
	component: "sb-product-highlight" | "sb-product-highlight-square";
	_uid: string;
	name: string;
	background?: BlokAsset;
	link?: BlokLink;
	title: string;
	content: ISbRichtext;
	background_color?: BlokColorpicker;
	text_color?: BlokColorpicker;
	campaign_title: string;
	campaign_percent: string;
	campaign_cta_text: string;
	campaign_additional_content: string;
	logo?: BlokAsset;
	hide_campaign: boolean;
	campaign_percentage_color?: BlokColorpicker;
	button_background_color?: BlokColorpicker;
	button_text_color?: BlokColorpicker;
	button_text_size?: number;
}

export interface InfoBlokContent {
	component: "sb-info" | "sb-info-item";
	_uid: string;
	image: BlokAsset;
	link?: BlokLink;
	title: string;
	content_title: string;
	has_background: boolean;
	icon: Icon;
	ingress: string;
	content: BlokComponent[];
	button_link?: BlokLink;
	button_text?: string;
	enable_darkening?: boolean;
}

export interface SocialBlokContent {
	component: "sb-social";
	_uid: string;
	images: BlokAsset[];
	links?: SbLink[];
	title: string;
	content: ISbRichtext;
}

export interface DiscountBlokContent {
	component: "sb-discount-badge";
	sale_percent: string;
	theme: string;
	_uid: string;
}

export interface TextColumnBlokContent {
	component: "sb-text-columns" | "sb-text-column";
	_uid: string;
	title: string;
	icon?: Icon;
	icon_image?: BlokAsset;
	has_background: boolean;
	button_text: string;
	button_link: BlokLink;
	content: BlokComponent[];
}

export interface CategoryUuids {
	id: string;
}

export interface CatalogBlokContent {
	component: "sb-catalog";
	title: string;
	ingress: string;
	link: BlokLink;
	image: BlokAsset;
	_uid: string;
}

export interface CategoryCtaBlokContent {
	component: "sb-category-cta";
	title: string;
	link: BlokLink;
	link_title: string;
	_uid: string;
}

export interface ProductSafetyBlokContent {
	component: "sb-product-safety" | "sb-product-safety-container";
	title: string;
	icon: Icon;
	image?: BlokAsset;
	_uid: string;
	content: ISbRichtext[];
}

export interface CategoriesListingBlokComponent {
	component: "sb-categories-listing-big";
	category: CategoryUuids[];
}

export interface CategoryCardBlokComponent {
	component: "sb-category-card-big" | "sb-category-card";
	title: string;
	link?: BlokLink;
	image?: BlokAsset;
	logo?: BlokAsset;
	icon?: BlokAsset;
	link_title: string;
	short_description: string;
	name: string;
}

export interface CategoryBlokContent {
	_editable: string;
	_uid: string;
	component: string;
	image: BlokAsset;
	title: string;
	name: string;
	content: CategoryCardBlokComponent;
}

export interface ContentImage {
	component: "sb-content-image";
	_editable: string;
	_uid: string;
	image: BlokAsset;
}

export interface ProductImageLiftLinkProduct {
	_uid: string;
	title: string;
	plugin: string;
	source: SbLiftProduct;
	description: string;
}

export interface SbLiftProduct {
	id: string;
	sku: string;
	name: string;
	type: string;
}

export interface ProductImageLiftLink {
	cached_url: string;
	fieldtype: string;
	id: string;
	linktype: string;
	url: string;
	Links: [];
	position_x: number;
	position_y: number;
	product: ProductImageLiftLinkProduct;
}

export interface ProductImageTextLift {
	component: "sb-product-image-text-lift";
	_uid: string;
	title: string;
	title_color: BlokColorpicker;
	background_image: BlokAsset;
	background_color: BlokColorpicker;
	cta_color: BlokColorpicker;
	cta_text: string;
	cta_text_color: BlokColorpicker;
	link: BlokLink;
}

export interface ProductImageLift {
	component: "sb-product-image-lift";
	_uid: string;
	image: BlokAsset;
	images: BlokAsset[];
	title: "string";
	link_to_room: BlokLink;
	product_links?: ProductImageLiftLink[];
	cta_text?: string;
	cta_text_color: string;
	cta_background_color: string;
	text_color: BlokColorpicker;
	enable_text_shadow: boolean;
	use_autocrop: boolean;
	enable_slideshow: boolean;
	enable_background_animation?: boolean;
}

export interface ProductImageLifts {
	component: "sb-product-image-lifts";
	_uid: string;
	images: ProductImageLift[];
}

export enum ProductMediaSubtype {
	benefitImage = "benefit-image",
	other = "other",
}

export type ProductMediaDataFragmentWithSubtype =
	SimpleProductDataFragment["medias"][0] & {
		subtype: string;
	};

export interface SubmenuBlokContent {
	_editable: string;
	_uid: string;
	component: string;
	title: string;
	referring_blok: string;
	link: BlokLink[];
}

export interface MenuItemBlokContent {
	_editable: string;
	_uid: string;
	component: string;
	title: string;
	link: BlokLink;
	icon: Icon;
}

interface Link {
	id: number;
	slug: string;
	name: string;
	is_folder: boolean;
	parent_id: number;
	published: boolean;
	position: number;
	uuid: string;
	is_startpage: boolean;
	real_path: string;
}

interface LinksResponse {
	links: Record<string, Link>;
}

interface LatLonCoordinates {
	lat: number;
	lon: number;
}

interface CategoryMenu {
	_uid: string;
	component: "sb-category-menu";
	sub_categories: never[];
	_editable: string;
}

interface BlokAssetLink {
	_uid: string;
	menu_image: BlokAsset;
	link: BlokLink;
	title: string;
	_editable: string;
}

interface BlokLinkMenu {
	_uid: string;
	component: "sb-link-menu";
	links: BlokAssetLink[];
	_editable: string;
}

interface TypeFormComponent {
	_uid: string;
	component: "sb-typeform";
	typeform_id: string;
	anchor_id: string;
	button_text: string;
}

interface SwiperComponent {
	_uid: string;
	component: "sb-swiper" | "sb-swiper-wide" | "sb-swiper-narrow";
	content: BlokComponent[];
	button_style: string;
	enable_autoplay: boolean;
	disclaimer?: DisclaimerBlokComponent;
	show_next_previous_buttons?: boolean;
}

export interface IframeComponent {
	component: "sb-iframe";
	_uid: string;
	url: string;
	height: StoryblokSliderPlugin;
	attributes?: string;
}

export interface DesignerBlokComponent {
	component: "sb-designer";
	_uid: string;
	title: string;
	description: ISbRichtext;
	button_text: string;
	url: string;
	height: StoryblokSliderPlugin;
	width: StoryblokSliderPlugin;
	attributes?: string;
	content_alignment: string;
}

export interface OutletsType {
	[index: number]: OutletType;
}

export interface OutletType {
	dimension_value?: string;
	id: number;
	name: string;
	value: string;
}

export interface OutletHousrsTable {
	tbody?: {
		_uid: string;
		component: "table-row";
		body: Array<{
			value?: string;
		}>;
	}[];
	thead?: unknown[];
	fieldtype?: string;
}
export interface OutletContentType {
	component: "sb-page-outlet-ktrl";
	_uid: string;
	meta: MetaFieldsPlugin;
	hours: ISbRichtext;
	phone: string;
	city?: string;
	email?: string;
	postal: string;
	address: string;
	contact: string;
	articles: unknown[];
	location: string;
	nosto_id: string;
	_editable: string;
	languages: LanguageBlokComponent[];
	hours_table?: OutletHousrsTable;
	outlet_body: string;
	outlet_name: string;
	outlet_type: string;
	trailer_rental: boolean;
	warehouse_body: string;
	outlet_location: string;
	social_facebook: string;
	warehouse_hours: string;
	social_instagram: string;
	social_pinterest: string;
	warehouse_pickup: boolean;
	pickup_available?: boolean;
	tailer_rental_description: {type: string; content: unknown[]};
	name?: string;
	cta_blok?: CtaBlokComponent[];
	outlet_images: BlokAsset[];
	socials?: LanguageBlokComponent[];
}

export interface HeaderColors {
	background_color: BlokColorpicker;
	font_color?: string;
	logo_background?: string;
}

export interface BackgroundBlokContent {
	component: "sb-background";
	content: BlokComponent[];
	background_color?: BlokColorpicker;
	_uid: string;
}

export interface TitleBlokContent {
	component: "sb-title";
	_uid: string;
	title: string;
	ingress: ISbRichtext;
	hide_title: boolean;
	title_tag: string;
	background_color?: BlokColorpicker;
	text_color?: BlokColorpicker;
}

export interface HeadingBlokContent {
	component: "sb-heading";
	_uid: string;
	title: string;
	heading: string;
	content: ISbRichtext;
	font_size: BlokSliderPlugin;
}

export interface Table {
	_uid?: string;
	tbody: TableBlokContent[];
	thead: TableBlokContent[];
	fieldtype: string;
	value: string;
}

export interface TableBlokContent {
	component: "sb-table";
	_uid: string;
	table?: Table;
	_editable: string;
	body?: Table[];
	value?: string;
}

interface VimeoResponse {
	account_type?: string;
	author_name?: string;
	author_url?: string;
	description?: string;
	duration: number;
	height: number;
	html: string;
	is_plus?: string;
	provider_name?: string;
	provider_url: string;
	thumbnail_height: number;
	thumbnail_url: string;
	thumbnail_url_with_play_button: string;
	thumbnail_width: number;
	title?: string;
	type: string;
	upload_date: string;
	uri: string;
	version: string;
	video_id: number;
	width: number;
}

interface VimeoOEmbedResponse {
	url: string;
	response: VimeoResponse;
}

export interface VimeoPlugin {
	plugin: string;
	filename?: string;
	vimeo_raw: string;
	vimeo_oembed: VimeoOEmbedResponse;
}

export interface BlokVideoComponent {
	component: "sb-video";
	_uid: string;
	player_alt: string;
	video: VimeoPlugin;
	video_url?: string;
	vimeo_raw?: string;
}

export interface BlokHeroVideoComponent {
	component: "sb-hero-video";
	_uid: string;
	player_alt: string;
	video: VimeoPlugin;
	autoplay?: boolean;
	muted?: boolean;
	show_controls?: boolean;
	thumbnail?: BlokAsset;
	aspect_ratio?: string;
	alt?: string;
	fitting_strategy?: "cover" | "contain" | string;
	video_url?: string;
	vimeo_raw?: string;
	alternative_video: BlokAsset;
	cta_title: string;
	cta_link: BlokLink;
}

export interface BlokCtaVideoComponent {
	component: "sb-cta-video";
	_uid: string;
	video_blok?: BlokHeroVideoComponent[];
	video: BlokAsset;
	player_alt: string;
	player_show_controls: boolean;
	cta_title: string;
	cta_link: BlokLink;
	link_color?: BlokColorpicker;
	link_background_color?: BlokColorpicker;
}

export interface BlokDisclaimerComponent {
	_uid: string;
	component: "sb-disclaimer";
	content: ISbRichtext;
}

export interface FreshworksButtonBlokComponent {
	component: "sb-freshworks-button";
	button_text: string;
	_uid: string;
}

export interface DisclaimerBlokComponent {
	component: "sb-disclaimer";
	content: ISbRichtext;
	_uid: string;
}

export interface FlocklerBlokComponent {
	component: "sb-flockler";
	_uid: string;
	flockler_id: string;
}

export interface PromotionCountdownComponent {
	component: "sb-promotion-countdown";
	_uid: string;
	title: string;
	ingress: ISbRichtext;
	start_date: Date;
	end_date: Date;
	text_color: BlokColorpicker;
	background_color: BlokColorpicker;
	centered_content: boolean;
}

export interface ListingAdComponent {
	component: "sb-listing-ad";
	title: string;
	content: string;
	background_image: BlokAsset;
	background_color: BlokColorpicker;
	link: BlokLink;
	video_link: VimeoPlugin;
	use_as_slideshow: boolean;
	images: BlokAsset[];
}

interface GtmPopupComponent {
	component: "sb-popup" | "sb-popup-member-collection";
	_uid: string;
	name: string;
	gtm_id: string;
	ingress: ISbRichtext;
	is_enabled: boolean;
	content: ISbRichtext;
	cta_text: string;
	link: BlokLink;
	background_color: BlokColorpicker;
	font_color: BlokColorpicker;
	background_image: BlokAsset;
	logo: BlokAsset;
	enable_background_darkening: boolean;
}

export interface SocialLinkBlokContent {
	component: "sb-social-link";
	_uid: string;
	icon: Icon;
	link: BlokLink;
	link_text: string;
}

export interface CtaLinkContent {
	component: "sb-cta-link";
	link: BlokLink;
	link_text: string;
	button_color: BlokColorpicker;
	text_color: BlokColorpicker;
}

export interface ContentButton {
	component: "sb-content-button";
	size?: "sm" | "md" | "lg";
	font_color?: BlokColorpicker;
	background_color?: BlokColorpicker;
	link: BlokLink;
	title: string;
}

export interface FlowboxComponent {
	component: "sb-flowbox";
	key: string;
	tags: string;
}

export interface FAQBlokComponent {
	component: "sb-faq-item";
	_uid: string;
	question?: ISbRichtext;
	answer?: ISbRichtext;
}

export interface FAQSectionBlokComponent {
	component: "sb-faq-section";
	_uid: string;
	items: FAQBlokComponent[];
}

export type BlokComponent = {_uid?: string} & (
	| BaseBlokComponent
	| ArticleBlokComponent
	| BrandBlokContent
	| CtaBlokComponent
	| CtaDiscountBlokComponent
	| NostoBlockComponent
	| LinkBlokContent
	| LiftBlokComponent
	| BrandsBlokComponent
	| BrandBlokComponent
	| DropdownBlokContent
	| DropdownContentBlokContent
	| ArticlesBlokComponent
	| SlideBlokComponent
	| ProductHighlightBlokContent
	| InfoBlokContent
	| SocialBlokContent
	| DiscountBlokContent
	| TextColumnBlokContent
	| CatalogBlokContent
	| ProductSafetyBlokContent
	| BrandsListingBlokComponent
	| BrandCardBlokComponent
	| ArticlesFeedBlokComponent
	| OpenJobsBlokcomponent
	| CustomerServiceTitleBlokComponent
	| LanguageBlokComponent
	| CategoryCtaBlokContent
	| CategoriesListingBlokComponent
	| CategoryCardBlokComponent
	| CategoryBlokContent
	| CategoryUuids
	| SubmenuBlokContent
	| MenuItemBlokContent
	| Link
	| LinksResponse
	| LatLonCoordinates
	| CategoryMenu
	| TypeFormComponent
	| SwiperComponent
	| IframeComponent
	| HeaderColors
	| ProductHighlightInfoBlokContent
	| BackgroundBlokContent
	| TitleBlokContent
	| HeadingBlokContent
	| BlokVideoComponent
	| TableBlokContent
	| FreshworksButtonBlokComponent
	| CustomerServiceContentBlokComponent
	| FlocklerBlokComponent
	| PromotionCountdownComponent
	| DisclaimerBlokComponent
	| CategoryCtaBlokComponent
	| ProductImageLift
	| ListingAdComponent
	| BlokDisclaimerComponent
	| BlokLinkMenu
	| ProductHighlightsBlokContent
	| CtaSimpleWithDiscountBlokComponent
	| CtaCampaignHero
	| CtaCampaignHeroBox
	| ProductImageTextLift
	| GtmPopupComponent
	| DesignerBlokComponent
	| DiscountsBlokComponent
	| SocialLinkBlokContent
	| BlokHeroVideoComponent
	| BlokCtaVideoComponent
	| ContentButton
	| OutletContentType
	| FlowboxComponent
	| FAQBlokComponent
	| FAQSectionBlokComponent
	| OutletContentType
);
